export const isDateInFuture = (mmddyyyy: string): boolean => {
    if (!mmddyyyy) return false;
    try {
        const target = new Date(mmddyyyy);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate());
        return targetDay.getTime() > today.getTime();
    } catch {
        return false;
    }
};

export type SessionSummaryData = {
    attendance: Record<string, any>;
    classData: Record<string, any>;
    studentData: Record<string, any>;
};

export interface AttendanceRecord {
    checkedIn: boolean;
    checkedOut?: boolean;
    checkInTime?: any;
    checkOutTime?: any;
}

export type AttendanceUpdateType = 'checkin' | 'checkout';

export const getDefaultAttendance = (): AttendanceRecord => ({
    checkedIn: false,
    checkedOut: false,
    checkInTime: null,
    checkOutTime: null,
});

export const computeNextAttendance = (
    prevAttendance: AttendanceRecord | undefined,
    type: AttendanceUpdateType
): { nextAttendance: AttendanceRecord; hasAttendanceChanged: boolean } => {
    const attendance = prevAttendance || getDefaultAttendance();
    let nextAttendance: AttendanceRecord = { ...attendance };

    if (type === 'checkin') {
        if (attendance.checkedIn) {
            nextAttendance = getDefaultAttendance();
        } else {
            nextAttendance = {
                checkedIn: true,
                checkedOut: false,
                checkInTime: new Date(),
                checkOutTime: null,
            };
        }
    } else {
        if (!attendance.checkedIn) {
            return { nextAttendance: attendance, hasAttendanceChanged: false };
        }

        if (attendance.checkedOut) {
            nextAttendance = {
                ...nextAttendance,
                checkedOut: false,
                checkOutTime: null,
            };
        } else {
            nextAttendance = {
                ...nextAttendance,
                checkedOut: true,
                checkOutTime: new Date(),
            };
        }
    }

    const hasAttendanceChanged = JSON.stringify(attendance) !== JSON.stringify(nextAttendance);
    return { nextAttendance, hasAttendanceChanged };
};

export const normalizeAttendanceRecord = (raw: any): AttendanceRecord => {
    if (!raw || typeof raw !== 'object') return getDefaultAttendance();
    if (raw.checkedIn !== undefined || raw.checkedOut !== undefined) {
        return {
            checkedIn: !!raw.checkedIn,
            checkedOut: !!raw.checkedOut,
            checkInTime: raw.checkInTime ?? null,
            checkOutTime: raw.checkOutTime ?? null,
        };
    }
    if (typeof raw === 'boolean') {
        return {
            checkedIn: raw,
            checkedOut: false,
            checkInTime: raw ? new Date() : null,
            checkOutTime: null,
        };
    }
    return getDefaultAttendance();
};

export const collectStudentSummary = (
    sessionDays: string[] = [],
    sessionSummary: SessionSummaryData,
    userId?: string
) => {
    if (!userId) return { comments: [], photos: [] };
    const comments: any[] = [];
    const photos: any[] = [];
    for (const d of sessionDays) {
        const sc = sessionSummary?.studentData?.[userId]?.[d];
        const cc = sessionSummary?.classData?.[d];
        if (sc?.comments && Array.isArray(sc.comments)) comments.push(...sc.comments.map((c: any) => ({...c, _date: d})));
        if (cc?.comments && Array.isArray(cc.comments)) comments.push(...cc.comments.map((c: any) => ({...c, _date: d})));
        if (sc?.photos && Array.isArray(sc.photos)) photos.push(...sc.photos.map((p: any) => ({...p, _date: d})));
        if (cc?.photos && Array.isArray(cc.photos)) photos.push(...cc.photos.map((p: any) => ({...p, _date: d})));
    }
    return { comments, photos };
};

export const buildSessionDays = (
    startDate: string,
    endDate: string,
    timeOps: { getOfficialStartDate: (d: string) => Date; formatToMMDDYYYY: (d: Date) => string }
): string[] => {
    const days: string[] = [];
    if (!startDate || !endDate) return days;
    const start = timeOps.getOfficialStartDate(startDate);
    const end = timeOps.getOfficialStartDate(endDate);
    if (!start || !end) return days;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        days.push(timeOps.formatToMMDDYYYY(d));
    }
    return days;
};

export const reconstructSessionSummary = (data: any) => {
    const reconstructed: SessionSummaryData = {
        attendance: data?.attendance || {},
        classData: {},
        studentData: {}
    } as any;

    if (!data) return reconstructed;

    // Class data using || delimiter
    Object.keys(data).forEach(key => {
        if (key.startsWith('classData||')) {
            const parts = key.split('||');
            if (parts.length >= 3) {
                const date = parts[1];
                const type = parts[2];
                if (!reconstructed.classData[date]) reconstructed.classData[date] = {};
                (reconstructed.classData as any)[date][type] = data[key];
            }
        } else if (key.startsWith('classData_')) {
            // backward compat
            const parts = key.substring('classData_'.length).split('_');
            if (parts.length >= 2) {
                const date = parts.slice(0, -1).join('_');
                const type = parts[parts.length - 1];
                if (!reconstructed.classData[date]) reconstructed.classData[date] = {};
                (reconstructed.classData as any)[date][type] = data[key];
            }
        }
    });

    // Student data using || delimiter
    Object.keys(data).forEach(key => {
        if (key.startsWith('studentData||')) {
            const parts = key.split('||');
            if (parts.length >= 4) {
                const userId = parts[1];
                const date = parts[2];
                const type = parts[3];
                if (!reconstructed.studentData[userId]) (reconstructed.studentData as any)[userId] = {};
                if (!(reconstructed.studentData as any)[userId][date]) (reconstructed.studentData as any)[userId][date] = {};
                (reconstructed.studentData as any)[userId][date][type] = data[key];
            }
        } else if (key.startsWith('studentData_')) {
            // backward compat
            const withoutPrefix = key.substring('studentData_'.length);
            const parts = withoutPrefix.split('_');
            if (parts.length >= 3) {
                const type = parts[parts.length - 1];
                const remaining = parts.slice(0, -1).join('_');
                const dateMatch = remaining.match(/(.+?)_(\d+\/\d+\/\d+)$/);
                if (dateMatch) {
                    const userId = dateMatch[1];
                    const date = dateMatch[2];
                    if (!reconstructed.studentData[userId]) (reconstructed.studentData as any)[userId] = {};
                    if (!(reconstructed.studentData as any)[userId][date]) (reconstructed.studentData as any)[userId][date] = {};
                    (reconstructed.studentData as any)[userId][date][type] = data[key];
                }
            }
        }
    });

    return reconstructed;
};

export const formatSessionDuration = (start?: string, end?: string) => {
    if (!start || !end) return '';
    try {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
    } catch {
        return '';
    }
};

export const getAttendanceCountForDate = (attendance: Record<string, any>, date: string, registeredUsers: any[]) => {
    const dayAttendance = attendance?.[date] || {};
    const validChildIds = Object.keys(dayAttendance).filter(userId => 
        userId.startsWith('child_') || registeredUsers.some(u => u.userId === userId)
    );
    return validChildIds.filter(userId => normalizeAttendanceRecord(dayAttendance[userId]).checkedIn).length;
};


